import React, { useCallback, memo, useMemo } from 'react';
import { View } from 'react-native';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import useColors from 'hooks/useColors';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import StatusIcon from 'design-system/StatusIcon';
import BlurAmountView from 'design-system/BlurAmountView';
import useStyles from 'hooks/useStyles';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { formatPlural } from 'utils/formatting';
import { SellOrderType, } from '../types';
import FormattedAmount from './FormattedAmount';
import webActivityRowStyle from './webActivityRowStyle';
import useStockMeta from '../hooks/useStockMeta';
import { formatDateForItem, formatExpiryDate, formatNumberOfSharesAmount } from '../utils';
import { ROW_ITEM_VERTICAL_PADDING, ROW_ITEM_HORIZONTAL_PADDING } from '../constants';
import { selectTradingAccount } from '../reducers/selectors';
import { setSelectedTradingAccountId } from '../actions';
import { isWeb } from '../../../constants';
const strings = {
    buy: 'BUY',
    sell: 'SELL',
    switch: 'SWITCH',
    dividendReinvested: 'DIVIDEND REINVESTED',
};
const ActivityItem = ({ item, isFirstInSection, isLastInSection, withWrapper, bounceCard, tradingAccountId, }) => {
    const stockCache = useStockMeta(item.assetId);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const onPress = useCallback(() => {
        if ('operation' in item) {
            if (item.operation === 'switch') {
                const { assetId, switchOrderId, switchAssetId, id, status } = item;
                if (switchOrderId && switchAssetId) {
                    navigation.navigate('InvestSwitchOrder', {
                        id,
                        status,
                        assetId,
                        switchOrderId,
                        switchAssetId,
                    });
                }
            }
            else {
                navigation.navigate('InvestOrder', { order: item });
            }
        }
        if ('type' in item) {
            if (item.status === 'completed') {
                navigation.navigate('InvestReceipt', { activity: item });
            }
            else {
                if (tradingAccountId) {
                    dispatch(setSelectedTradingAccountId(tradingAccountId));
                }
                if (item.funding?.method &&
                    (item.status === 'pending' || // filtering pending
                        (item.status === 'failed' &&
                            'estimatedDateOfCollection' in item.funding &&
                            item.funding.estimatedDateOfCollection)) // filtering all failed activity with no collection
                ) {
                    navigation.navigate('DepositAndBuyAssetTimeline', {
                        assetId: item.assetId,
                        amount: item.amount,
                        timestamp: item.timestamp,
                        status: item.status,
                        fundingMethod: item.funding.method,
                        estimatedDateOfCollection: 'estimatedDateOfCollection' in item.funding ? item.funding.estimatedDateOfCollection : undefined,
                        estimatedDateOfInvesting: 'estimatedDateOfInvesting' in item.funding ? item.funding.estimatedDateOfInvesting : undefined,
                        dateOfCollection: 'dateOfCollection' in item.funding ? item.funding.dateOfCollection : undefined,
                        dateOfInvesting: 'dateOfInvesting' in item.funding ? item.funding.dateOfInvesting : undefined,
                    });
                    return;
                }
                navigation.navigate('InvestOrder', {
                    order: {
                        id: item.id,
                        symbol: item.symbol,
                        status: item.status,
                        operation: item.type,
                        assetId: item.assetId,
                        timestamp: item.timestamp,
                        estimated: { amount: item.amount, quantity: item.quantity },
                    },
                    activity: item,
                });
            }
        }
    }, [item, tradingAccountId]);
    const operation = 'operation' in item ? item.operation : item.type;
    const amount = useMemo(() => {
        if ('orderType' in item) {
            if (item.orderType === SellOrderType.LIMIT && item.limitPrice) {
                return { value: item.limitPrice, currency: 'USD' };
            }
            if (item.orderType === SellOrderType.STOP && item.stopPrice) {
                return { value: item.stopPrice, currency: 'USD' };
            }
        }
        return 'operation' in item ? item.filled?.amount || item.estimated?.amount || item.requested?.amount : item.amount;
    }, [item]);
    const quantity = 'operation' in item ? item.filled?.quantity || item.estimated?.quantity || item.requested?.quantity : item.quantity;
    const borderStyles = useMemo(() => [isFirstInSection && styles.firstItem, isLastInSection && styles.lastItem], [isFirstInSection, styles.firstItem, styles.lastItem, isLastInSection]);
    const webRowStyle = withWrapper ? webActivityRowStyle : undefined;
    const operationText = useMemo(() => {
        if ('isDividendReinvest' in item && item.isDividendReinvest) {
            return strings.dividendReinvested;
        }
        if ('orderType' in item) {
            if (item.orderType === SellOrderType.LIMIT) {
                return `LIMIT ${operation === 'buy' ? strings.buy : strings.sell}`;
            }
            if (item.orderType === SellOrderType.STOP) {
                return `STOP ${operation === 'buy' ? strings.buy : strings.sell}`;
            }
        }
        if (operation === 'switch') {
            return strings.switch;
        }
        return operation === 'buy' ? strings.buy : strings.sell;
    }, [item, operation]);
    const dateToShow = useMemo(() => {
        const prefix = (() => {
            if (item.status === 'queued') {
                return 'Executes';
            }
            if (item.status === 'pending') {
                return 'Arrives';
            }
            return '';
        })();
        if ('orderType' in item && (item.orderType === SellOrderType.LIMIT || item.orderType === SellOrderType.STOP)) {
            if (item.timeInForce) {
                return formatExpiryDate(item.timeInForce, 'Expires');
            }
        }
        else if (item.timestamp) {
            return formatDateForItem(item.timestamp, prefix);
        }
        return '';
    }, [item]);
    const StatusInfo = useMemo(() => {
        switch (item.status) {
            case 'completed':
                return (<View style={styles.infoRow}>
            <StatusIcon state="done" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 Positive>
              Filled
            </Text>
          </View>);
            case 'pending':
                return (<View style={styles.infoRow}>
            <StatusIcon state="in-progress" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 StatusInformational>
              Pending
            </Text>
          </View>);
            case 'queued':
                return (<View style={styles.infoRow}>
            <StatusIcon state="in-progress" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 StatusInformational>
              Queued
            </Text>
          </View>);
            case 'cancelled':
                return (<View style={styles.infoRow}>
            <StatusIcon state="cancelled-red" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 StatusInformational>
              Cancelled
            </Text>
          </View>);
            case 'failed':
                return (<View style={styles.infoRow}>
            <StatusIcon state="cancelled-red" width={16} height={16}/>
            <Spacer w={8}/>
            <Text TextThin-14 StatusInformational>
              Failed
            </Text>
          </View>);
            default:
                return null;
        }
    }, [item.status, styles.infoRow]);
    const rightInfoItems = useMemo(() => {
        const strikeThrough = item.status === 'cancelled' || item.status === 'failed';
        const items = [
            <>
        <Spacer w={8}/>
        {strikeThrough ? (<FormattedAmount amount={amount} Secondary strikeThrough/>) : (<FormattedAmount amount={amount}/>)}
      </>,
            <>
        <Spacer w={8}/>
        <BlurAmountView enabled={scrambled}>
          <Text TextThin-14 Gray>
            {typeof quantity === 'number' ? (<Text TextThin-14 Gray>
                <Text NumbersThin-14 Gray>
                  {formatNumberOfSharesAmount(quantity)}
                </Text>{' '}
                {formatPlural('share', 'shares', quantity)}
              </Text>) : (' ')}
          </Text>
        </BlurAmountView>
      </>,
        ];
        if ((tradingAccount?.isAlpaca || item.status === 'completed' || item.status === 'failed') && dateToShow) {
            items.push(<>
          <Spacer w={8}/>
          <Text TextThin-14 Gray numberOfLines={1}>
            {dateToShow}
          </Text>
        </>);
        }
        else {
            items.unshift(null);
        }
        return items;
    }, [amount, dateToShow, item.status, quantity, scrambled, tradingAccount?.isAlpaca]);
    const content = (<View style={webRowStyle ? [styles.itemContainer, webRowStyle] : styles.itemContainer}>
      <Logo R44 uri={stockCache?.iconUrl} resizeMode="contain"/>
      <Spacer w={16}/>
      <View style={styles.infoContainer}>
        <View style={styles.infoRow}>
          <View style={styles.flex}>
            <Text Text-14 Gray>
              {operationText}
            </Text>
          </View>
          {rightInfoItems[0]}
        </View>
        <View style={styles.infoRow}>
          <View style={styles.flex}>
            <Text Text-16 style={styles.flexText} numberOfLines={1}>
              {stockCache?.name ?? item.symbol}
            </Text>
          </View>
          {rightInfoItems[1]}
        </View>
        <View style={styles.infoRow}>
          <View style={styles.flex}>{StatusInfo}</View>
          {rightInfoItems[2]}
        </View>
      </View>
    </View>);
    if (bounceCard) {
        return (<TouchableBounceCard onDark noMarginBottom onPress={onPress} style={styles.bounceCard}>
        {content}
      </TouchableBounceCard>);
    }
    return (<View style={withWrapper ? [styles.wrapper, borderStyles] : null}>
      <TouchableHighlight hoverOnLight onCard={isWeb} onPress={onPress} activeOpacity={1} style={withWrapper && !isWeb ? borderStyles : null} underlayColor={colors.background.underlay}>
        {content}
      </TouchableHighlight>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    // On web the row is padded by 8 horizontally, so the card adds the other 8. The row
    // already has 16 above and below, so the card adds nothing vertically.
    bounceCard: {
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    wrapper: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    itemContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rem(ROW_ITEM_HORIZONTAL_PADDING),
        paddingVertical: rem(ROW_ITEM_VERTICAL_PADDING),
        // On web the card is padded by 8 and each row by 8, so the hover highlight sits inside the card
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    infoContainer: { flex: 1 },
    infoRow: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flexText: {
        flexShrink: 1,
    },
    firstItem: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(isWeb && { paddingTop: rem(8) }),
    },
    lastItem: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(isWeb && { paddingBottom: rem(8) }),
    },
    flex: {
        flex: 1,
    },
}));
const PureActivityItem = memo(ActivityItem);
// PureActivityItem.whyDidYouRender = true;
export default PureActivityItem;
